import { useCallback, useEffect } from "react";

import { ClassSessionCategory } from "components/server-types";
import { useCapabilities } from "store/capability-hooks";
import { storeApi, useAppSelector } from "store/index";
import { selectActiveClassSession, selectAvActiveClassUsersIds } from "store/selectors";
import {
  selectCalculatedListForActiveParticipants,
  selectIsParticipantBarLeader,
} from "store/selectors/virtual-class-users-sorting";

export const useParticipantBarObserver = () => {
  const { canAuthorSection } = useCapabilities();
  const isLeaderOfParticipantBar = useAppSelector(selectIsParticipantBarLeader);
  const activeClassSession = useAppSelector(selectActiveClassSession);
  const participantBarList = useAppSelector(selectAvActiveClassUsersIds);
  const calculatedList = useAppSelector(selectCalculatedListForActiveParticipants);

  const updateParticipantBarList = useCallback(
    (newParticipantBarList: string[]) => {
      if (!activeClassSession) return;
      storeApi.class_sessions.partial_update({
        id: activeClassSession.id,
        av_active_class_users_ids: newParticipantBarList,
      });
    },
    [activeClassSession],
  );

  useEffect(() => {
    // Only section staff can PATCH class_sessions; "leader" can be a student when only students are present
    if (
      !canAuthorSection ||
      !isLeaderOfParticipantBar ||
      !activeClassSession ||
      activeClassSession.category !== ClassSessionCategory.VIRTUAL
    )
      return;
    // If the result of the calculation is the same as the current participant bar list, do nothing.
    if (
      participantBarList.length === calculatedList.length &&
      participantBarList.every((id, index) => id === calculatedList[index])
    )
      return;
    updateParticipantBarList(calculatedList);
  }, [
    canAuthorSection,
    isLeaderOfParticipantBar,
    calculatedList,
    activeClassSession,
    participantBarList,
    updateParticipantBarList,
  ]);
};
